import { useState, useEffect } from 'react';
import { API, getAuthHeader } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Send, Plus, X, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

function NotificacaoItem({ notificacao }) {
  const formatDate = function(d) {
    return d ? new Date(d).toLocaleString('pt-BR') : '-';
  };

  const getTipoColor = function(t) {
    if (t === 'cobranca') return 'bg-red-100 text-red-700';
    if (t === 'promocional') return 'bg-green-100 text-green-700';
    if (t === 'informativo') return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="p-4 hover:bg-slate-50" data-testid={'notificacao-' + notificacao.id}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-medium text-slate-800">{notificacao.titulo}</h3>
          <p className="text-slate-600 text-sm mt-1">{notificacao.mensagem}</p>
        </div>
        <span className={'text-xs px-2 py-1 rounded-full ' + getTipoColor(notificacao.tipo)}>
          {notificacao.tipo}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />{formatDate(notificacao.created_at)}
        </span>
        <span>Por: {notificacao.enviado_por_nome}</span>
      </div>
    </div>
  );
}

export default function MensagensPage() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    titulo: '',
    mensagem: '',
    tipo: 'geral'
  });

  useEffect(function() { loadNotificacoes(); }, []);

  const loadNotificacoes = async function() {
    try {
      const res = await axios.get(API + '/notificacoes', { headers: getAuthHeader() });
      setNotificacoes(res.data.notificacoes || []);
    } catch (error) {
      toast.error('Erro ao carregar');
    }
    setLoading(false);
  };

  const enviarNotificacao = async function(e) {
    e.preventDefault();
    if (!form.titulo || !form.mensagem) {
      toast.error('Preencha todos os campos');
      return;
    }
    try {
      await axios.post(API + '/notificacoes', form, { headers: getAuthHeader() });
      toast.success('Notificação criada!');
      setShowModal(false);
      setForm({ titulo: '', mensagem: '', tipo: 'geral' });
      loadNotificacoes();
    } catch (error) {
      toast.error('Erro ao enviar');
    }
  };

  const renderNotificacoes = function() {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      );
    }
    
    if (notificacoes.length === 0) {
      return (
        <div className="p-8 text-center text-slate-500">
          <Bell className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p>Nenhuma mensagem enviada</p>
        </div>
      );
    }

    return (
      <div className="divide-y">
        {notificacoes.map(function(n) {
          return <NotificacaoItem key={n.id} notificacao={n} />;
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="mensagens-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Bell className="w-7 h-7 text-blue-500" />
            Mensagens
          </h1>
          <p className="text-slate-500 text-sm">Envie notificações para seus clientes</p>
        </div>
        <Button onClick={function() { setShowModal(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="nova-mensagem-btn">
          <Plus className="w-4 h-4 mr-2" />Nova Mensagem
        </Button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-slate-800">Histórico de Mensagens</h2>
        </div>
        {renderNotificacoes()}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold">Nova Mensagem</h2>
              <button onClick={function() { setShowModal(false); }} className="text-slate-400 hover:text-slate-600" data-testid="close-modal-btn">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={enviarNotificacao} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Título</label>
                <Input 
                  value={form.titulo} 
                  onChange={function(e) { setForm({...form, titulo: e.target.value}); }}
                  placeholder="Ex: Aviso importante"
                  required
                  data-testid="titulo-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mensagem</label>
                <textarea 
                  value={form.mensagem} 
                  onChange={function(e) { setForm({...form, mensagem: e.target.value}); }}
                  placeholder="Digite a mensagem..."
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                  data-testid="mensagem-textarea"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select 
                  value={form.tipo} 
                  onChange={function(e) { setForm({...form, tipo: e.target.value}); }}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="tipo-select"
                >
                  <option value="geral">Geral</option>
                  <option value="informativo">Informativo</option>
                  <option value="cobranca">Cobrança</option>
                  <option value="promocional">Promocional</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={function() { setShowModal(false); }}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="enviar-notificacao-btn">
                  <Send className="w-4 h-4 mr-2" />Enviar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
